import type {
	AasApiAdapter,
	CreateTriggerInput,
	MintBadgeInput,
	TriggerRunFilters,
	UpdateBadgeInput,
	UpdateTriggerInput,
} from "../adapter";
import { isHttpError, type QueryParams, requestJson } from "../http";
import type {
	AgentTrigger,
	AgentTriggerRun,
	BadgeStyle,
	CreatedAgentTrigger,
	TriggerBadge,
	WorkspaceTrigger,
} from "../types";
import type {
	BackendBadge,
	BackendTrigger,
	BackendTriggerCreateResponse,
	BackendTriggerRun,
	BackendWorkspaceTrigger,
} from "./backendTypes";

export function mapTrigger(row: BackendTrigger): AgentTrigger {
	return {
		id: row.id,
		name: row.name,
		message: row.message,
		tokenPrefix: row.tokenPrefix,
		enabled: row.enabled,
		onFailureTriggerId: row.onFailureTriggerId ?? null,
		lastRunAt: row.lastRunAt ?? null,
		lastRunStatus:
			row.lastRunStatus === "ok" || row.lastRunStatus === "error" ? row.lastRunStatus : null,
		lastRunError: row.lastRunError ?? null,
		lastConversationId: row.lastConversationId ?? null,
		createdAt: row.createdAt,
		updatedAt: row.updatedAt,
	};
}

export function mapTriggerRun(row: BackendTriggerRun): AgentTriggerRun {
	return {
		id: row.id,
		triggerId: row.triggerId,
		triggerName: row.triggerName ?? null,
		conversationId: row.conversationId ?? null,
		status: row.status === "ok" || row.status === "error" ? row.status : "running",
		error: row.error ?? null,
		source:
			row.source === "badge" || row.source === "failure-notification" ? row.source : "webhook",
		startedAt: row.startedAt,
		finishedAt: row.finishedAt ?? null,
	};
}

const BADGE_STYLES: BadgeStyle[] = ["ember", "graphite", "compact", "status"];

function mapBadge(row: BackendBadge): TriggerBadge {
	return {
		id: row.id,
		triggerId: row.triggerId,
		token: row.token,
		tokenPrefix: row.tokenPrefix,
		style: BADGE_STYLES.includes(row.style as BadgeStyle) ? (row.style as BadgeStyle) : "ember",
		label: row.label,
		rateLimitPerHour: row.rateLimitPerHour,
		showReply: row.showReply,
		allowInput: row.allowInput,
		runCount: row.runCount,
		svgPath: row.svgPath,
		pagePath: row.pagePath,
		createdAt: row.createdAt,
		updatedAt: row.updatedAt,
	};
}

function badgeBase(slug: string, triggerId: string): string {
	return `/api/agents/${encodeURIComponent(slug)}/triggers/${encodeURIComponent(triggerId)}/badge`;
}

function triggersBase(slug: string): string {
	return `/api/agents/${encodeURIComponent(slug)}/triggers`;
}

export const triggersApi: AasApiAdapter["triggers"] = {
	async list(slug) {
		const rows = await requestJson<BackendTrigger[]>(triggersBase(slug));
		return rows.map(mapTrigger);
	},
	async listAll() {
		const rows = await requestJson<BackendWorkspaceTrigger[]>("/api/triggers");
		return rows.map(
			(row): WorkspaceTrigger => ({
				...mapTrigger(row),
				agentSlug: row.agentSlug,
				agentName: row.agentName,
			}),
		);
	},
	async runs(slug, filters?: TriggerRunFilters) {
		const query: QueryParams = {
			triggerId: filters?.triggerId,
			limit: filters?.limit,
		};
		const rows = await requestJson<BackendTriggerRun[]>(
			`/api/agents/${encodeURIComponent(slug)}/runs`,
			{ query },
		);
		return rows.map(mapTriggerRun);
	},
	async create(slug, input: CreateTriggerInput) {
		const created = await requestJson<BackendTriggerCreateResponse>(triggersBase(slug), {
			method: "POST",
			body: { name: input.name, message: input.message },
		});
		return {
			...mapTrigger(created),
			token: created.token,
			hookPath: created.hookPath,
		} satisfies CreatedAgentTrigger;
	},
	async update(slug, id, input: UpdateTriggerInput) {
		const updated = await requestJson<BackendTrigger>(
			`${triggersBase(slug)}/${encodeURIComponent(id)}`,
			{ method: "PATCH", body: input },
		);
		return mapTrigger(updated);
	},
	async delete(slug, id) {
		await requestJson<void>(`${triggersBase(slug)}/${encodeURIComponent(id)}`, {
			method: "DELETE",
		});
	},
	async badge(slug, triggerId) {
		try {
			const row = await requestJson<BackendBadge>(badgeBase(slug, triggerId));
			return mapBadge(row);
		} catch (error) {
			if (isHttpError(error) && error.status === 404) {
				return null;
			}
			throw error;
		}
	},
	async mintBadge(slug, triggerId, input: MintBadgeInput) {
		const row = await requestJson<BackendBadge>(badgeBase(slug, triggerId), {
			method: "POST",
			body: input,
		});
		return mapBadge(row);
	},
	async updateBadge(slug, triggerId, input: UpdateBadgeInput) {
		const row = await requestJson<BackendBadge>(badgeBase(slug, triggerId), {
			method: "PATCH",
			body: input,
		});
		return mapBadge(row);
	},
	async revokeBadge(slug, triggerId) {
		await requestJson<void>(badgeBase(slug, triggerId), { method: "DELETE" });
	},
};
